import { CSSProperties, ReactNode, useEffect, useMemo, useState } from "react";
import styles from "@shared/styles/components/Tables.module.css";
import { Loader } from "@shared/designSystem/Loader";

export type TableCellAlignment = "left" | "center" | "right";

export interface TableColumn {
    id: string;
    label: ReactNode;
    width?: string;
    align?: TableCellAlignment;
    className?: string;
}

export interface GridTableProps {
    columns: TableColumn[];
    children: ReactNode;
    rowTemplate?: CSSProperties["gridTemplateColumns"];
    className?: string;
    headerClassName?: string;
    bodyClassName?: string;
    minWidth?: CSSProperties["minWidth"];
    isLoading?: boolean;
}

export interface PaginatedGridTableProps<T> {
    columns: TableColumn[];
    items: T[];
    renderRow: (item: T, index: number) => ReactNode;
    pageSize?: number;
    initialPage?: number;
    rowTemplate?: CSSProperties["gridTemplateColumns"];
    className?: string;
    headerClassName?: string;
    bodyClassName?: string;
    footerClassName?: string;
    minWidth?: CSSProperties["minWidth"];
    isLoading?: boolean;
    emptyState?: ReactNode;
}

export interface GridTableRowProps {
    children: ReactNode;
    className?: string;
}

export interface GridTableCellProps {
    children: ReactNode;
    align?: TableCellAlignment;
    className?: string;
}

interface AlignmentClassByValue {
    left: string;
    center: string;
    right: string;
}

const alignmentClassByValue: AlignmentClassByValue = {
    left: styles["align-left"],
    center: styles["align-center"],
    right: styles["align-right"],
};

const classNames = (...classes: Array<string | undefined>) => {
    return classes.filter(Boolean).join(" ");
};

const resolveGridTemplate = (
    columns: TableColumn[],
    rowTemplate?: CSSProperties["gridTemplateColumns"],
) => {
    if (rowTemplate) {
        return rowTemplate;
    }

    if (columns.some((column) => !!column.width)) {
        return columns.map((column) => column.width ?? "1fr").join(" ");
    }

    return `repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))`;
};

export const GridTable = ({
    columns,
    children,
    rowTemplate,
    className,
    headerClassName,
    bodyClassName,
    minWidth = "640px",
    isLoading = false,
}: GridTableProps) => {
    const gridTemplate = resolveGridTemplate(columns, rowTemplate);

    return (
        <div className={classNames(styles["table-wrapper"], className)}>
            <div
                className={classNames(styles["table-header"], headerClassName)}
                style={{ gridTemplateColumns: gridTemplate, minWidth }}
            >
                {columns.map((column) => (
                    <div
                        key={column.id}
                        className={classNames(
                            styles["table-cell"],
                            styles["table-header-cell"],
                            alignmentClassByValue[column.align ?? "left"],
                            column.className,
                        )}
                    >
                        {column.label}
                    </div>
                ))}
            </div>
            <div className={classNames(styles["table-body"], bodyClassName)}>
                <div
                    className={classNames(
                        styles["table-grid"],
                        isLoading ? styles["table-grid-loading"] : undefined,
                    )}
                    style={{
                        ["--ds-table-grid-template" as string]: gridTemplate,
                        ["--ds-table-min-width" as string]:
                            typeof minWidth === "number"
                                ? `${minWidth}px`
                                : minWidth,
                    }}
                >
                    {isLoading ? <Loader /> : children}
                </div>
            </div>
        </div>
    );
};

export const GridTableRow = ({ children, className }: GridTableRowProps) => {
    return <div className={classNames(styles["table-row"], className)}>{children}</div>;
};

export const GridTableCell = ({
    children,
    align = "left",
    className,
}: GridTableCellProps) => {
    return (
        <div className={classNames(styles["table-cell"], alignmentClassByValue[align], className)}>
            {children}
        </div>
    );
};

const getVisiblePages = (currentPage: number, totalPages: number, maxVisible = 5) => {
    if (totalPages <= maxVisible) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - half);
    let end = start + maxVisible - 1;

    if (end > totalPages) {
        end = totalPages;
        start = totalPages - maxVisible + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

export const PaginatedGridTable = <T,>({
    columns,
    items,
    renderRow,
    pageSize = 10,
    initialPage = 1,
    rowTemplate,
    className,
    headerClassName,
    bodyClassName,
    footerClassName,
    minWidth = "640px",
    isLoading = false,
    emptyState = "No hay datos para mostrar.",
}: PaginatedGridTableProps<T>) => {
    const normalizedPageSize = Math.max(1, pageSize);
    const totalPages = Math.max(1, Math.ceil(items.length / normalizedPageSize));
    const safeInitialPage = Math.min(Math.max(initialPage, 1), totalPages);
    const [currentPage, setCurrentPage] = useState(safeInitialPage);
    const gridTemplate = resolveGridTemplate(columns, rowTemplate);

    useEffect(() => {
        setCurrentPage((previousPage) => Math.min(Math.max(previousPage, 1), totalPages));
    }, [totalPages]);

    const start = (currentPage - 1) * normalizedPageSize;
    const end = start + normalizedPageSize;
    const paginatedItems = useMemo(() => items.slice(start, end), [items, start, end]);
    const visiblePages = useMemo(
        () => getVisiblePages(currentPage, totalPages),
        [currentPage, totalPages],
    );

    return (
        <div className={classNames(styles["table-wrapper"], styles["table-wrapper-auto"], className)}>
            <div
                className={classNames(styles["table-header"], headerClassName)}
                style={{ gridTemplateColumns: gridTemplate, minWidth }}
            >
                {columns.map((column) => (
                    <div
                        key={column.id}
                        className={classNames(
                            styles["table-cell"],
                            styles["table-header-cell"],
                            alignmentClassByValue[column.align ?? "left"],
                            column.className,
                        )}
                    >
                        {column.label}
                    </div>
                ))}
            </div>
            <div className={classNames(styles["table-body"], styles["table-body-auto"], bodyClassName)}>
                <div
                    className={classNames(
                        styles["table-grid"],
                        isLoading ? styles["table-grid-loading"] : undefined,
                    )}
                    style={{
                        ["--ds-table-grid-template" as string]: gridTemplate,
                        ["--ds-table-min-width" as string]:
                            typeof minWidth === "number"
                                ? `${minWidth}px`
                                : minWidth,
                    }}
                >
                    {isLoading ? (
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px'}}>
                            <Loader />
                        </div>
                    ) : paginatedItems.length ? (
                        paginatedItems.map((item, index) => renderRow(item, start + index))
                    ) : (
                        <div className={styles["table-empty"]}>{emptyState}</div>
                    )}
                </div>
            </div>
            <div className={classNames(styles["table-footer"], footerClassName)}>
                <div className={styles["table-pagination"]}>
                    <button
                        type="button"
                        className={styles["table-pagination-button"]}
                        onClick={() => setCurrentPage((previousPage) => Math.max(previousPage - 1, 1))}
                        disabled={currentPage === 1}
                    >
                        {"Anterior".localized()}
                    </button>
                    {visiblePages.map((pageNumber) => (
                        <button
                            key={pageNumber}
                            type="button"
                            className={classNames(
                                styles["table-pagination-button"],
                                currentPage === pageNumber
                                    ? styles["table-pagination-button-active"]
                                    : undefined,
                            )}
                            onClick={() => setCurrentPage(pageNumber)}
                            aria-current={currentPage === pageNumber ? "page" : undefined}
                        >
                            {pageNumber}
                        </button>
                    ))}
                    <button
                        type="button"
                        className={styles["table-pagination-button"]}
                        onClick={() =>
                            setCurrentPage((previousPage) => Math.min(previousPage + 1, totalPages))
                        }
                        disabled={currentPage === totalPages}
                    >
                        {"Siguiente".localized()}
                    </button>
                </div>
            </div>
        </div>
    );
};
